'use client'

import { useState, type ComponentProps } from 'react'

import CitationForm, {
  type CitationSubmission,
} from './components/CitationForm'
import CitationResults from './components/CitationResults'
import { openCheckout } from '@/lib/paddle/checkout'
import { useToolRunOrCheckout } from '@/hooks/useToolRunOrCheckout'
import { canRunWithoutConfirmation } from '@/lib/radar/entity-types'
import ToolAiCitationTrackerRef from '@/components/reference/ToolAiCitationTrackerRef'
import {
  ToolLiveError,
  ToolLiveStatus,
  ToolPayWall,
} from '@/components/reference/tool-live'
import '@/components/reference/tool-run.css'
import '@/components/reference/tool-page.css'
import '@/components/reference/tool-live.css'

export default function AiCitationTrackerPage() {
  const [label, setLabel] = useState('')
  const [entityError, setEntityError] = useState<string | null>(null)
  // Frozen at submit so editing the form after the wall appears cannot change
  // what the $5 checkout pays to run.
  const [submitted, setSubmitted] = useState<CitationSubmission | null>(null)
  // Owner/paid → full result inline (free); everyone else → 402 → the $5 wall.
  const {
    run,
    loading,
    error: runError,
    result,
    walled,
    reset,
  } = useToolRunOrCheckout<ComponentProps<typeof CitationResults>['result']>(
    '/api/tools/citation-check'
  )

  function handleSubmit(submission: CitationSubmission) {
    // Defense in depth at the owner/paid inline-run boundary. CitationForm owns
    // the confirmation UI, but this page is the component that actually spends.
    if (
      !canRunWithoutConfirmation(
        submission.entity,
        submission.url,
        submission.brand
      )
    ) {
      setSubmitted(null)
      setLabel('')
      setEntityError('Confirm the company for this website before running.')
      reset()
      return
    }

    const frozen: CitationSubmission = {
      brand: submission.brand.trim(),
      url: submission.url.trim(),
      category: submission.category.trim(),
      entity: {
        ...submission.entity,
        evidence: [...submission.entity.evidence],
        conflicts: [...submission.entity.conflicts],
      },
    }
    setEntityError(null)
    setLabel(frozen.brand || frozen.url)
    setSubmitted(frozen)
    void run({
      brand: frozen.brand,
      url: frozen.url,
      category: frozen.category,
      entity: frozen.entity,
    })
    setTimeout(() => {
      document
        .getElementById('citation-output')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 50)
  }

  function handleDirty() {
    setSubmitted(null)
    setLabel('')
    setEntityError(null)
    reset()
  }

  function handleCheckout() {
    if (
      !submitted ||
      !canRunWithoutConfirmation(
        submitted.entity,
        submitted.url,
        submitted.brand
      )
    ) {
      setSubmitted(null)
      setEntityError('Confirm the company for this website before checkout.')
      reset()
      return
    }

    void openCheckout('pack-single', 'tool_teaser', {
      source: 'tool_run',
      toolSlug: 'citation-check',
      brand: submitted.brand,
      url: submitted.url,
      category: submitted.category,
      entityDomain: submitted.entity.domain,
      entitySource: submitted.entity.source,
      entityConfidence: submitted.entity.confidence,
      entityConfirmedByUser: String(submitted.entity.confirmedByUser),
      entityDetectedBrand: submitted.entity.detectedBrand,
      entityResolverVersion: submitted.entity.resolverVersion,
    })
  }

  return (
    <div className='shell pg-tool pg-tool-ai-citation-tracker'>
      <ToolAiCitationTrackerRef
        form={
          <>
            <CitationForm
              variant='reference'
              onSubmit={handleSubmit}
              onDirty={handleDirty}
              isLoading={loading}
            />
            {entityError && (
              <p role='alert' className='run__note run__error tool-live__after'>
                <i />
                {entityError}
              </p>
            )}
          </>
        }
        output={
          <>
            {(loading || runError || result || walled) && (
              <div id='citation-output' className='max-w-4xl mx-auto mb-12'>
                {loading && (
                  <ToolLiveStatus tool='AI Citation Tracker'>
                    Checking citations for {label}…
                  </ToolLiveStatus>
                )}

                {runError && !loading && (
                  <ToolLiveError>{runError}</ToolLiveError>
                )}

                {result && !loading && <CitationResults result={result} />}

                {walled && submitted && !loading && (
                  <ToolPayWall
                    tool='AI Citation Tracker'
                    title={<>Run the citation check for {label}</>}
                    onCheckout={handleCheckout}
                  >
                    Pay a one-time $5 to check <strong>{label}</strong> across
                    all supported AI engines: brand recognition, competitive
                    visibility, URL citations, sentiment, and the fixes. Your
                    report opens right here.
                  </ToolPayWall>
                )}
              </div>
            )}
          </>
        }
      />
    </div>
  )
}
